import { data, Link, useLoaderData } from 'react-router'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { Card, CardContent } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.js'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { PAGE_MEASURE, PAGE_PROSE } from '#app/utils/layout.ts'
import { type Route } from './+types/index.ts'

export async function loader({}: Route.LoaderArgs) {
	const cities = await prisma.city.findMany({
		where: {
			businesses: {
				some: {
					status: 'published',
				},
			},
		},
		select: {
			id: true,
			name: true,
			slug: true,
			_count: {
				select: {
					businesses: {
						where: {
							status: 'published',
						},
					},
				},
			},
		},
		orderBy: {
			name: 'asc',
		},
	})

	const totalProviders = await prisma.business.count({
		where: { status: 'published' },
	})

	return data({ cities, totalProviders })
}

// Breadcrumb handle for automatic breadcrumb generation
export const handle = {
	breadcrumb() {
		return { name: 'Cities', url: '/cities' }
	},
	seo({
		loaderData,
		origin,
		canonical,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
		canonical: string
	}): SeoProps {
		const structuredData =
			loaderData.cities.length > 0
				? [
						buildItemListLd({
							name: 'Dallas-Fort Worth cities covered by DFW Professionals',
							items: loaderData.cities.map((city) => ({
								name: city.name,
								url: `${origin}/cities/${city.slug}`,
							})),
						}),
					]
				: undefined

		// "Vetted" again, in all three. Replaced with the counts, which are
		// both defensible and more useful to someone deciding whether this
		// page covers where they live.
		const description = `Browse businesses in ${loaderData.cities.length} Dallas-Fort Worth cities, covering ${loaderData.totalProviders.toLocaleString()} listed businesses. Each city is ranked over the businesses that serve it, scored against a published rubric.`

		return {
			title: 'Dallas-Fort Worth cities | DFW Professionals',
			description,
			canonical,
			openGraph: {
				title: 'Dallas-Fort Worth cities | DFW Professionals',
				description,
				type: 'website',
				url: canonical,
			},
			twitter: {
				card: 'summary_large_image',
				title: 'Dallas-Fort Worth cities | DFW Professionals',
				description,
			},
			structuredData,
		}
	},
}

export default function CitiesPage() {
	const { cities, totalProviders } = useLoaderData<typeof loader>()

	return (
		<>
			{/* ═══════════════════════════════════════════════════════════════════
			    HERO SECTION
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="pt-16 pb-12 md:pt-24 md:pb-16">
				<div className={PAGE_MEASURE}>
					<h1 className="mb-4 font-serif text-3xl tracking-tight md:text-4xl">
						Dallas-Fort Worth cities
					</h1>
					{/*
					 * States what a city page actually is, rather than asserting how
					 * the metroplex behaves. The previous version claimed pricing and
					 * availability vary by city, which is plausible, unmeasured, and
					 * rendered identically across all 55 of them.
					 */}
					<p
						className={`text-muted-foreground mb-6 text-lg leading-relaxed ${PAGE_PROSE}`}
					>
						A business is listed in every city it serves, so each city ranks
						only the businesses that reach it. That ordering differs from the
						metro-wide one, which is why these pages exist separately.
					</p>
					<div className="flex flex-wrap gap-4 text-sm">
						<div className="flex items-center gap-2">
							<Icon name="map-pin" className="text-muted-foreground size-4" />
							<span className="tabular-nums">{cities.length} cities</span>
						</div>
						<div className="flex items-center gap-2">
							<Icon name="check" className="size-4 text-green-600" />
							<span className="tabular-nums">
								{totalProviders.toLocaleString()} businesses listed
							</span>
						</div>
					</div>
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    CITY GRID
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<div className="mb-8">
						<h2 className="mb-2 font-serif text-xl tracking-tight">
							Find your city
						</h2>
						<p className="text-muted-foreground text-sm">
							The count is how many listed businesses serve that city.
						</p>
					</div>

					{cities.length === 0 ? (
						<div className="bg-muted rounded-lg py-16 text-center">
							<Icon
								name="map-pin"
								className="text-muted-foreground mx-auto mb-4 size-12"
							/>
							<h3 className="mb-2 text-xl font-semibold">No Cities Yet</h3>
							<p className="text-muted-foreground">
								Cities will appear here once added.
							</p>
						</div>
					) : (
						<div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
							{cities.map((city) => (
								<Link
									key={city.id}
									to={`/cities/${city.slug}`}
									className="group"
								>
									<Card className="transition-all hover:shadow-md">
										<CardContent className="flex items-center justify-between p-4">
											<div>
												<h3 className="font-medium group-hover:underline">
													{city.name}
												</h3>
												<p className="text-muted-foreground text-sm tabular-nums">
													{city._count.businesses.toLocaleString()} business
													{city._count.businesses !== 1 ? 'es' : ''}
												</p>
											</div>
											<Icon
												name="arrow-right"
												className="text-muted-foreground size-4 transition-transform group-hover:translate-x-1"
											/>
										</CardContent>
									</Card>
								</Link>
							))}
						</div>
					)}
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    FAQ SECTION
			    ═══════════════════════════════════════════════════════════════════ */}
			{/*
			 * The band spans the viewport; its contents sit on the page measure.
			 * That is why this section keeps its own wrapper rather than moving
			 * inside a single page-level container.
			 */}
			<section className="bg-muted/30 border-y py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<div className={PAGE_PROSE}>
						<h2 className="mb-8 font-serif text-2xl tracking-tight">
							Common questions
						</h2>

						{/*
						 * Every previous answer here was an unmeasured claim about the
						 * DFW market: that pricing is "generally similar" across cities,
						 * that smaller cities have "less competition", that wait times
						 * differ by city. We measure none of that. What we can answer is
						 * how these pages are built, which is also what a reader needs in
						 * order to trust the ordering they are looking at.
						 */}
						<Accordion type="single" collapsible className="w-full">
							<AccordionItem value="city-not-listed">
								<AccordionTrigger>
									What if my city isn&apos;t listed?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									A city appears here once at least one published business lists
									it as an area it serves. If yours is missing, a business
									serving it is the thing that adds it, so try the nearest
									listed city or{' '}
									<Link className="underline underline-offset-4" to="/submit">
										submit a business
									</Link>
									.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="provider-coverage">
								<AccordionTrigger>
									Can the same business appear under several cities?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									Yes. A business is listed under every city it serves, and its
									listing page shows all of them. Its rank can differ from city
									to city, because each city is scored only over the businesses
									that reach it.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="how-ranked">
								<AccordionTrigger>
									How is the order on a city page decided?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									By the same published rubric used everywhere on the site,
									applied to the public reviews each business has already
									received. Nothing about the city changes the weights. See{' '}
									<Link
										className="underline underline-offset-4"
										to="/scoring-method"
									>
										how we score
									</Link>
									.
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</div>
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    BROWSE BY CATEGORY CTA
			    ═══════════════════════════════════════════════════════════════════ */}
			{/*
			 * The one block on the page that stays centred, and the only one that
			 * earns it: a call to action is not reading material, so breaking the
			 * spine here reads as emphasis rather than as drift.
			 */}
			<section className="py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<div className="mx-auto max-w-2xl text-center">
						<h2 className="mb-4 text-xl font-medium">
							Know what you need? Start there instead.
						</h2>
						<p className="text-muted-foreground mb-6">
							Category pages include DFW-wide context, common red flags, and
							recommendations organized by specialty.
						</p>
						<Link
							to="/categories"
							className="text-primary inline-flex items-center gap-2 font-medium hover:underline"
						>
							Browse by category
							<Icon name="arrow-right" className="size-4" />
						</Link>
					</div>
				</div>
			</section>
		</>
	)
}
